import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import FilterPanel from './components/FilterPanel';
import SortControls from './components/SortControls';
import BeansGrid from './components/BeansGrid';
import Pagination from './components/Pagination';
import { FullWidthSection, ContentWrapper } from '../../components/ui/Layout';
import { beansService } from '../../services/supabaseService';
import { useLanguage } from '../../contexts/LanguageContext';

const BrowseBeans = () => {
  const { language } = useLanguage();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter state
  const [filters, setFilters] = useState({
    search: '',
    countries: [],
    priceMin: '',
    priceMax: '',
    processing: [],
    elevationMin: '',
    elevationMax: '',
    productionYear: '',
    certifications: []
  });

  // Sort state
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);

  // Real beans data from Supabase
  const [allBeans, setAllBeans] = useState([]);
  const [filteredBeans, setFilteredBeans] = useState([]);

  useEffect(() => {
    loadBeans();
  }, []);

  const loadBeans = async () => {
    try {
      setLoading(true);
      setError(null);
      const beans = await beansService?.getAll();
      setAllBeans(beans || []);
      setFilteredBeans(beans || []);
    } catch (err) {
      console.error('Failed to load beans:', err);
      setError(err?.message || 'Failed to load coffee beans');
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort beans
  useEffect(() => {
    let filtered = [...allBeans];

    // Apply search filter
    if (filters?.search) {
      const searchTerm = filters?.search?.toLowerCase();
      filtered = filtered?.filter((bean) =>
        bean?.nameEn?.toLowerCase()?.includes(searchTerm) ||
        bean?.nameZh?.includes(searchTerm) ||
        bean?.farm?.nameEn?.toLowerCase()?.includes(searchTerm)
      );
    }

    // Apply country filter
    if (filters?.countries && filters?.countries?.length > 0) {
      filtered = filtered?.filter((bean) => filters?.countries?.includes(bean?.country?.code?.toLowerCase()));
    }

    // Apply price range filter
    if (filters?.priceMin) {
      filtered = filtered?.filter((bean) => bean?.priceUsdPerKg >= parseFloat(filters?.priceMin));
    }
    if (filters?.priceMax) {
      filtered = filtered?.filter((bean) => bean?.priceUsdPerKg <= parseFloat(filters?.priceMax));
    }

    // Apply processing filter
    if (filters?.processing && filters?.processing?.length > 0) {
      filtered = filtered?.filter((bean) => filters?.processing?.includes(bean?.processingType));
    }

    // Apply elevation filter
    if (filters?.elevationMin) {
      filtered = filtered?.filter((bean) => bean?.altitudeM >= parseInt(filters?.elevationMin));
    }
    if (filters?.elevationMax) {
      filtered = filtered?.filter((bean) => bean?.altitudeM <= parseInt(filters?.elevationMax));
    }

    // Apply production year filter
    if (filters?.productionYear) {
      filtered = filtered?.filter((bean) => bean?.productionYear?.toString() === filters?.productionYear);
    }

    // Apply certifications filter
    if (filters?.certifications && filters?.certifications?.length > 0) {
      filtered = filtered?.filter((bean) =>
        filters?.certifications?.some((cert) => bean?.certifications?.includes(cert))
      );
    }

    // Apply sorting
    filtered?.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'name':
          aValue = language === 'en' ? a?.nameEn : a?.nameZh;
          bValue = language === 'en' ? b?.nameEn : b?.nameZh;
          break;
        case 'price':
          aValue = a?.priceUsdPerKg;
          bValue = b?.priceUsdPerKg;
          break;
        case 'production_year':
          aValue = a?.productionYear;
          bValue = b?.productionYear;
          break;
        case 'elevation':
          aValue = a?.altitudeM;
          bValue = b?.altitudeM;
          break;
        case 'country':
          aValue = language === 'en' ? a?.country?.nameEn : a?.country?.nameZh;
          bValue = language === 'en' ? b?.country?.nameEn : b?.country?.nameZh;
          break;
        default:
          aValue = a?.nameEn;
          bValue = b?.nameEn;
      }

      if (typeof aValue === 'string') {
        aValue = aValue?.toLowerCase();
        bValue = bValue?.toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    setFilteredBeans(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [filters, sortBy, sortOrder, allBeans, language]);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      search: '',
      countries: [],
      priceMin: '',
      priceMax: '',
      processing: [],
      elevationMin: '',
      elevationMax: '',
      productionYear: '',
      certifications: []
    };
    setFilters(clearedFilters);
  };

  const handleSortChange = (newSortBy, newSortOrder) => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Calculate pagination
  const totalItems = filteredBeans?.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentBeans = filteredBeans?.slice(startIndex, endIndex);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-[60px]">
        <FullWidthSection padding="">
          <ContentWrapper padding="">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-foreground mb-2">
                {language === 'en' ? 'Browse Coffee Beans' : '浏览咖啡豆'}
              </h1>
              <p className="text-muted-foreground">
                {language === 'en' ? 'Discover premium African green coffee beans from verified suppliers with detailed specifications and farm data.' : '发现来自经过验证的供应商的优质非洲生咖啡豆，包含详细规格和农场数据。'
                }
              </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-8">
              {/* Filter Sidebar */}
              <aside className="lg:w-80 flex-shrink-0">
                <FilterPanel
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  onClearFilters={handleClearFilters}
                  language={language}
                  isOpen={isFilterOpen}
                  onToggle={() => setIsFilterOpen(!isFilterOpen)} />

              </aside>

              {/* Main Content */}
              <div className="flex-1 min-w-0">
                {/* Sort Controls */}
                <SortControls
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSortChange={handleSortChange}
                  language={language}
                  resultsCount={totalItems} />


                {/* Beans Grid */}
                <BeansGrid
                  beans={currentBeans}
                  language={language}
                  loading={loading}
                  error={error} />


                {/* Pagination */}
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  itemsPerPage={itemsPerPage}
                  onPageChange={handlePageChange}
                  language={language} />

              </div>
            </div>
          </ContentWrapper>
        </FullWidthSection>
      </main>
    </div>);

};

export default BrowseBeans;